import { debounce } from '@ember/runloop';

import type Highcharts from 'highcharts';
import type { Series, SeriesOptionsType } from 'highcharts';

import type { BlockThemeConfig, DChartInterface } from 'webapp-types';

import type VisualizationRadialProgress from 'webapp/models-custom/visualization/radial-progress';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import ProgressOrGaugeBaseRenderer from 'webapp/d-chart/renderer/progress-or-gauge-base';
import type DChartSeries from 'webapp/d-chart/series/series';

// a series as built here (its type comes from the chart options), with one point per series ring
type RadialSeriesOptions = Omit<
	Highcharts.SeriesSolidgaugeOptions,
	'type' | 'data' | 'colorIndex'
> & {
	data: (Omit<DChartInterface.ChartDataPoint, 'dbxPoint'> & {
		dbxPoint: DChartPrimaryPoint | null; // no primary point: an empty ring
		radius: string | number;
		innerRadius: string | number;
	})[];
	colorIndex?: string | number; // a class for custom colors
	dbxSeries: DChartSeries;
};

// Highcharts internals read on render: the main series' points with their shape and HTML data label
type RadialSeriesInternals = Highcharts.Series & {
	points: (Highcharts.Point & {
		shapeArgs: { innerR: number };
		dataLabel?: Highcharts.SVGElement & { div?: HTMLElement };
	})[];
};

export default class RadialProgressRenderer extends ProgressOrGaugeBaseRenderer {
	override get visualization(): VisualizationRadialProgress {
		return super.visualization as VisualizationRadialProgress;
	}

	get series() {
		return this.visualization.series;
	}

	get isSingleSeriesLayout() {
		// showing also the metric name and values
		return this.series.length < 2;
	}

	override get primaryPoint() {
		return this.isSingleSeriesLayout ? super.primaryPoint : null;
	}

	override get percent(): number {
		return this.isSingleSeriesLayout ? super.percent : 0;
	}

	get hasLegend() {
		return !this.visualization.isLegendOff;
	}

	get showValues() {
		return this.visualization.showValues;
	}

	override get baseOptions(): Highcharts.Options {
		const that = this;
		let options = super.baseOptions;

		options = this.mergeOptions(options, {
			chart: {
				type: 'solidgauge',
				events: {
					render: function (this: Highcharts.Chart, _event: Event) {
						that.updateDynamicElementsOnRender(this);

						if (that.hasLegend) {
							debounce(that, that.centerLegendItems, this.legend, 500);
						}
					},
				},
			},
			legend: {
				enabled: this.hasLegend,
			},
			pane: {
				startAngle: 0,
				endAngle: 360,
			},
			yAxis: {
				min: 0,
				max: 100,
				tickPositions: [],
			},
			plotOptions: {
				solidgauge: {
					linecap: 'round',
					rounded: true,
				},
				series: {
					animation: {
						duration: this.dChart.isAnimated ? 1000 : 0,
					},
					events: {
						click: function (
							this: Highcharts.Series,
							event: Highcharts.SeriesClickEventObject,
						) {
							that.onPointClick(
								(event.point as DChartInterface.ChartHoverPoint).dbxPoint,
								event,
								event.srcElement as SVGElement,
							);
						},
					},
				},
			},
		});

		return options;
	}

	override get dataOptions(): Highcharts.Options {
		const series = this.series,
			isSingleSeriesLayout = this.isSingleSeriesLayout;

		// when going from more panes to less, we need to "clear" the existing
		const existingPaneBgCount = this.dChart.hChart?.pane?.[0]?.background?.length || 0;
		const paneBgs = series.map((_s, idx) => {
			const { radius, innerRadius } = this.getRadiusesForIndex(idx);
			return {
				outerRadius: radius,
				innerRadius,
			};
		});
		for (let i = 0; i < existingPaneBgCount - series.length; i++) {
			paneBgs.push({ innerRadius: '', outerRadius: '' });
		}

		let options: Highcharts.Options = this.mergeOptions(super.dataOptions, {
			plotOptions: {
				solidgauge: {
					dataLabels: {
						enabled: isSingleSeriesLayout,
					},
				},
			},
			pane: {
				center: ['50%', this.hasLegend ? '40%' : '50%'],
				background: paneBgs,
			},
			tooltip: {
				enabled: !isSingleSeriesLayout,
			},
			legend: {
				enabled: this.hasLegend,
			},
		});

		if (!series.length) {
			return options;
		}

		let baseSeriesClass = '';

		const that = this;

		if (this.isSingleSeriesLayout) {
			const serie = series[0]!,
				percent = this.percent;

			if (serie.points[1]!.onClick) {
				baseSeriesClass = 'series-clickable';
			}

			const seriesClass = this.visualization.isSingleSeriesWithDefaultColor
				? `step-${this.gradientStepId} ${percent === 0 ? 'no-value' : ''}`
				: '';

			const radiuses = this.getRadiusesForIndex(0);

			options = this.mergeOptions(options, {
				title: {
					useHTML: true,
					verticalAlign: 'bottom',
				},
				plotOptions: {
					solidgauge: {
						dataLabels: {
							formatter: function (this: Highcharts.Point) {
								return that.formatSingleInfo();
							},
							padding: 0,
							useHTML: true,
							verticalAlign: 'middle',
						},
					},
				},
				series: [
					{
						className: `${seriesClass} ${baseSeriesClass}`,
						data: [
							{
								radius: radiuses.radius,
								innerRadius: radiuses.innerRadius,
								colorIndex: serie?.colorIndexOrClass || 0,
								y: percent,
								dbxPoint: this.primaryPoint,
							},
						],
						showInLegend: true,
						colorIndex: serie?.colorIndexOrClass || 0,
						dbxSeries: serie,
					},
				],
			});
		} else {
			const seriesData: RadialSeriesOptions[] = [];

			series.forEach((series, idx) => {
				const { radius, innerRadius } = this.getRadiusesForIndex(idx),
					{ primaryPoint } = this.getPointsForSeries(series);

				if (primaryPoint?.onClick) {
					baseSeriesClass = 'series-clickable';
				}

				seriesData.push({
					className: baseSeriesClass, // to reset single series class
					data: [
						{
							radius,
							innerRadius,
							colorIndex: series.colorIndexOrClass,
							y: this.getValueProgressForSeries(series),
							dbxPoint: primaryPoint,
						},
					],
					index: idx,
					showInLegend: true,
					colorIndex: series.colorIndexOrClass,
					dbxSeries: series,
				});
			});

			options = this.mergeOptions(options, {
				series: seriesData,
			});
		}

		return options;
	}

	override get sizeOptions(): Highcharts.Options {
		let options = super.sizeOptions;

		options = this.mergeOptions(options, {
			legend: this.defaultLegendOptions,
		});

		return options;
	}

	override get tooltipOptions(): Highcharts.Options {
		const options = super.tooltipOptions;

		options.tooltip!.positioner = function (
			width: number,
			height: number,
			_point: Highcharts.TooltipPositionerPointObject,
		) {
			const containerElement = this.chart.container;

			if (containerElement) {
				const pathElement = this.chart.hoverPoint?.graphic?.element,
					containerPos = containerElement.getBoundingClientRect(),
					elementPos = pathElement?.getBoundingClientRect() || { y: containerPos.y };
				return {
					x: containerPos.width / 2 - width / 2,
					y: elementPos.y - height - containerPos.y,
				};
			} else {
				return { x: 0, y: 0 };
			}
		};

		return options;
	}

	get gradientDefs(): DChartInterface.Def[] {
		const themeData = this.dChart.themeData,
			themeId = themeData.themeId,
			colors = themeData.colors,
			steps =
				(themeData.visualizationConfig.steps as
					BlockThemeConfig.GradientStep[] | undefined) || [],
			gradientDefs: DChartInterface.Def[] = [];

		steps.forEach((step, index: number) => {
			const stepId = index + 1;

			gradientDefs.push({
				tagName: 'linearGradient',
				id: `gradient-radial-progress-bar-step-${stepId}-${themeId}`,
				x1: 0,
				y1: 0,
				x2: 1,
				y2: 0,
				children: [
					{
						tagName: 'stop',
						offset: step.range[0],
						'stop-color': colors[step.gradient[0]],
					},
					{
						tagName: 'stop',
						offset: step.range[1],
						'stop-color': colors[step.gradient[1]],
					},
				],
			});
		});

		return gradientDefs;
	}

	override updateTheme(): void {
		this.visualization.updateColorIndexes();
		super.updateTheme();
	}

	getRadiusesForIndex(idx: number): { radius: string; innerRadius: string } {
		const seriesLength = this.series.length,
			centerSpaceReserved = 20,
			maxCircleThickness = 19,
			maxSize = this.hasLegend ? 80 : 100, // in percent
			circleThickness = Math.min(
				maxCircleThickness,
				(maxSize - centerSpaceReserved) / seriesLength,
			),
			circleThicknessWithMargin = circleThickness + 1;

		const radius = maxSize - idx * circleThicknessWithMargin,
			innerRadius = radius - circleThickness;

		return {
			radius: `${radius}%`,
			innerRadius: `${innerRadius}%`,
		};
	}

	/*(bcos of test)private */ formatSingleInfo(): string {
		if (!this.isSingleSeriesLayout || !this.firstSeries) {
			return '';
		}

		const { primaryPoint, minPoint, maxPoint } = this.getPointsForSeries(this.firstSeries);

		if (!primaryPoint || !maxPoint) {
			return '';
		}

		const { max } = this.getMinMaxValues(minPoint!, maxPoint);

		let changeBubbleText = '',
			valueStrText = '';

		// with loading flag we mark that this is auto-max and so the value should not be shown as compare
		if (max.value && !maxPoint.loading) {
			changeBubbleText = this.formatPercentBubble();
			valueStrText = `<div class="goal">${max.valueStr}</div>`;
		}

		let clickable = '';
		if (primaryPoint.onClick) {
			clickable = 'text-clickable';
		}

		return `<div class="info"><div class="value ${clickable}">${primaryPoint.valueStr}</div>${changeBubbleText}${valueStrText}</div>`;
	}

	private updateDynamicElementsOnRender(hChart: Highcharts.Chart): void {
		// position value labels
		const showValues = this.showValues;

		const yAxis = hChart.yAxis[0]!,
			chartPaneHalfHeight =
				(yAxis as Highcharts.Axis & { center?: number[] }).center?.[1] ||
				hChart.plotWidth / 2, // center: a Highcharts internal
			centerX = hChart.plotWidth / 2 + hChart.plotLeft,
			centerY = chartPaneHalfHeight + hChart.plotTop,
			axisRange = (yAxis.max || 0) - (yAxis.min || 0);

		hChart.series.forEach(function (
			series: Series & { customLabel?: Highcharts.SVGElement | null },
		) {
			if (series.customLabel) {
				series.customLabel.destroy();
				series.customLabel = null;
			}

			if (!showValues) {
				return;
			}

			const dbxSeries = (
				series.userOptions as SeriesOptionsType & { dbxSeries: DChartSeries }
			)?.dbxSeries;

			if (!dbxSeries) {
				return;
			}

			const dbxPoint = dbxSeries.points[1];
			const point = series.points[1];

			if (!dbxPoint || typeof point?.y === 'undefined' || !axisRange) {
				return;
			}

			const shapeArgs = point.shapeArgs;
			const radius = (shapeArgs?.r + shapeArgs?.innerR) / 2;
			const endAngle = 360;
			const extraDegrees = ((endAngle / 2) * 7) / (2 * chartPaneHalfHeight); // 7px label move is how many degrees forward
			const value = (point.y + extraDegrees) / axisRange;
			const angle = endAngle * value;
			const angleRad = ((angle - 90) * Math.PI) / 180;
			const endX = centerX + radius * Math.cos(angleRad),
				endY = centerY + radius * Math.sin(angleRad);

			series.customLabel = hChart.renderer
				.text(dbxPoint.valueStr, endX, endY)
				.attr({
					class: 'value',
					zIndex: 5,
					align: 'center',
					opacity: series.visible ? 0.5 : 0,
				})
				.add();
		});

		// scale middle info box for single series
		const mainSeries = hChart.series[0] as RadialSeriesInternals | undefined;
		if (mainSeries && this.isSingleSeriesLayout) {
			const point = mainSeries.points[1];

			if (point) {
				const innerR = point.shapeArgs.innerR,
					longestSideOfSquareToFitIntoInnerCircle = Math.sqrt(
						Math.pow((innerR - 10) * 2, 2) / 2,
					),
					infoEl = point.dataLabel?.div?.querySelector<HTMLElement>('.info');

				if (infoEl) {
					infoEl.style.transform = `scale(1)`; // reset size of info el box
					const infoElSize = infoEl.getBoundingClientRect(),
						longestSideOfInfoElSquare = Math.max(infoElSize.width, infoElSize.height),
						scaleModifier =
							longestSideOfSquareToFitIntoInnerCircle / longestSideOfInfoElSquare;

					infoEl.style.transform = `scale(${scaleModifier})`;
				}
			}
		}

		// calculate chart title height and adjust chart height
		const titleEl = this.dChart.node.querySelector('.highcharts-title'),
			titleElHeight = titleEl?.clientHeight || 0;

		if (titleElHeight && titleElHeight !== hChart.userOptions.chart?.marginBottom) {
			hChart.update({
				chart: {
					marginBottom: titleElHeight,
				},
			});
		}
	}
}
